import { Menu, Portal } from "@headlessui/react";
import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import ChevronDown from "../Icons/ChevronDown";

export type DropdownButtonItem = {
  id: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
};

type DropdownButtonColor =
  | "primary"
  | "accent"
  | "error"
  | "neutral"
  | "delete"
  | "excel";
type DropdownButtonStyle = "filled" | "ghost" | "outlined";

export type DropdownButtonProps = {
  action: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  items: DropdownButtonItem[];
  color?: DropdownButtonColor;
  style?: DropdownButtonStyle;
  disabled?: boolean;
  className?: string;
};

type SegmentTone = {
  rest: string;
  hover: string;
  open: string;
};

const FILLED: Record<DropdownButtonColor, SegmentTone> = {
  primary: {
    rest: "bg-accentDefault text-white",
    hover: "hover:bg-primary-dark",
    open: "bg-primary-dark text-white",
  },
  accent: {
    rest: "bg-Default text-white",
    hover: "hover:bg-accent-Default-dark",
    open: "bg-accent-Default-dark text-white",
  },
  error: {
    rest: "bg-primary-light text-primary-dark",
    hover: "hover:bg-primary-dark hover:text-white",
    open: "bg-primary-dark text-white",
  },
  neutral: {
    rest: "bg-gray-800 text-white",
    hover: "hover:bg-neutral-dark",
    open: "bg-neutral-dark text-white",
  },
  delete: {
    rest: "bg-primary text-white",
    hover: "hover:bg-primary-dark",
    open: "bg-primary-dark text-white",
  },
  excel: {
    rest: "bg-[#008000] text-white",
    hover: "hover:bg-[#005700]",
    open: "bg-[#005700] text-white",
  },
};

const OUTLINED: Record<DropdownButtonColor, SegmentTone & { frame: string }> = {
  primary: {
    rest: "bg-transparent text-primary",
    hover: "hover:bg-primary-light",
    open: "bg-primary-light text-primary",
    frame: "border border-primary",
  },
  accent: {
    rest: "bg-transparent text-Default",
    hover: "hover:bg-accent-Default-dark hover:text-white",
    open: "bg-accent-Default-dark text-white",
    frame: "border border-Default",
  },
  error: {
    rest: "bg-transparent text-primary-dark",
    hover: "hover:bg-primary-light",
    open: "bg-primary-light text-primary-dark",
    frame: "border border-primary-dark",
  },
  neutral: {
    rest: "bg-transparent text-gray-font",
    hover: "hover:bg-gray-font hover:text-white",
    open: "bg-gray-font text-white",
    frame: "border border-gray-font",
  },
  delete: {
    rest: "bg-transparent text-primary",
    hover: "hover:bg-primary hover:text-white",
    open: "bg-primary text-white",
    frame: "border border-primary",
  },
  excel: {
    rest: "bg-transparent text-[#008000]",
    hover: "hover:bg-[#008000] hover:text-white",
    open: "bg-[#008000] text-white",
    frame: "border border-[#008000]",
  },
};

const GHOST: Record<DropdownButtonColor, SegmentTone> = {
  primary: {
    rest: "bg-transparent text-primary",
    hover: "hover:bg-primary-light",
    open: "bg-primary-light text-primary",
  },
  accent: {
    rest: "bg-transparent text-Default",
    hover: "hover:text-accent-Default-dark",
    open: "text-accent-Default-dark",
  },
  error: {
    rest: "bg-transparent text-primary-dark",
    hover: "hover:bg-primary-light",
    open: "bg-primary-light text-primary-dark",
  },
  neutral: {
    rest: "bg-transparent text-gray-font",
    hover: "hover:text-neutral-dark",
    open: "text-neutral-dark",
  },
  delete: {
    rest: "bg-transparent text-primary",
    hover: "hover:bg-primary-light",
    open: "bg-primary-light text-primary",
  },
  excel: {
    rest: "bg-transparent text-[#008000]",
    hover: "hover:bg-[#008000]/10",
    open: "bg-[#008000]/10 text-[#008000]",
  },
};

function toneFor(
  color: DropdownButtonColor,
  style: DropdownButtonStyle
): SegmentTone {
  if (style === "filled") {
    return FILLED[color];
  }
  if (style === "outlined") {
    return OUTLINED[color];
  }
  return GHOST[color];
}

function segmentTone(tone: SegmentTone, open: boolean): string {
  return open ? tone.open : `${tone.rest} ${tone.hover}`;
}

function frameClass(
  color: DropdownButtonColor,
  style: DropdownButtonStyle
): string {
  return style === "outlined" ? OUTLINED[color].frame : "";
}

function dividerClass(style: DropdownButtonStyle): string {
  return style === "filled" ? "border-white/30" : "border-current/30";
}

function itemClasses(active: boolean, disabled: boolean): string {
  if (disabled) {
    return "block w-full cursor-not-allowed px-4 py-2 text-left text-sm text-gray-400";
  }
  return [
    "block w-full px-4 py-2 text-left text-sm text-gray-font",
    active ? "bg-gray-100" : "bg-white",
  ].join(" ");
}

const segmentClass =
  "inline-flex h-full shrink-0 items-center justify-center whitespace-nowrap border-0 font-decimal text-sm font-medium uppercase leading-none outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-inset";

const MENU_GAP_PX = 4;

type MenuCoords = {
  top: number;
  left: number;
  minWidth: number;
};

function DropdownButtonMenu({
  items,
  triggerRef,
}: {
  items: DropdownButtonItem[];
  triggerRef: RefObject<HTMLDivElement>;
}): JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<MenuCoords | null>(null);

  useLayoutEffect(() => {
    const update = () => {
      const trigger = triggerRef.current;
      if (!trigger) {
        return;
      }

      const rect = trigger.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight ?? 0;
      const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP_PX;
      const openUp =
        menuHeight > 0 && spaceBelow < menuHeight && rect.top > spaceBelow;
      const top = openUp
        ? rect.top - menuHeight - MENU_GAP_PX
        : rect.bottom + MENU_GAP_PX;
      const minWidth = rect.width;
      const maxLeft = window.innerWidth - minWidth - MENU_GAP_PX;

      setCoords({
        top: Math.max(MENU_GAP_PX, top),
        left: Math.max(MENU_GAP_PX, Math.min(rect.left, maxLeft)),
        minWidth,
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [triggerRef]);

  return (
    <Portal>
      <Menu.Items
        ref={menuRef}
        className="fixed z-[10020] rounded-md border border-gray-200 bg-white py-1 shadow-md focus:outline-none"
        style={{
          top: coords?.top ?? 0,
          left: coords?.left ?? 0,
          minWidth: coords?.minWidth,
          visibility: coords ? "visible" : "hidden",
        }}
      >
        {items.map((item) => (
          <Menu.Item key={item.id} disabled={item.disabled}>
            {({ active, disabled: itemDisabled }) => (
              <button
                type="button"
                disabled={itemDisabled}
                className={itemClasses(active, itemDisabled)}
                onClick={item.onSelect}
              >
                {item.label}
              </button>
            )}
          </Menu.Item>
        ))}
      </Menu.Items>
    </Portal>
  );
}

/**
 * NewButton CSS targets styled divs; :enabled and the medium/dropdown sizes
 * fight each other on real buttons. The frame only shares height and radius;
 * each half owns its own hover so the action and caret do not light up together.
 *
 * The menu is portaled: table bodies use overflow-x-auto, which clips an
 * absolutely positioned panel on the last rows.
 */
export default function DropdownButton({
  action,
  items,
  color = "accent",
  style = "filled",
  disabled = false,
  className = "",
}: DropdownButtonProps): JSX.Element {
  const actionDisabled = disabled || Boolean(action.disabled);
  const tone = toneFor(color, style);
  const triggerRef = useRef<HTMLDivElement>(null);

  return (
    <Menu as="div" className={`relative inline-flex shrink-0 ${className}`.trim()}>
      {({ open }) => (
        <>
          <div
            ref={triggerRef}
            className={`inline-flex h-8 shrink-0 overflow-hidden rounded-md ${frameClass(
              color,
              style
            )} ${disabled ? "pointer-events-none opacity-50" : ""}`}
          >
            <button
              type="button"
              className={`${segmentClass} px-3 ${
                actionDisabled
                  ? `${tone.rest} cursor-not-allowed opacity-50`
                  : segmentTone(tone, false)
              }`}
              disabled={actionDisabled}
              onClick={action.onClick}
            >
              {action.label}
            </button>
            <Menu.Button
              className={`${segmentClass} border-l px-2 ${dividerClass(
                style
              )} ${segmentTone(tone, open)}`}
              disabled={disabled}
            >
              <ChevronDown className="h-3.5 w-3.5 fill-current" />
            </Menu.Button>
          </div>
          {open ? (
            <DropdownButtonMenu items={items} triggerRef={triggerRef} />
          ) : null}
        </>
      )}
    </Menu>
  );
}
